import { useMemo } from "react";

import { authHeader, formsFetch, unwrap } from "../lib/formsApi";
import type { CodeOfConductSource } from "../questionnaire/codeOfConduct";

const isPdf = (name: string) => name.toLowerCase().endsWith(".pdf");

const sha256 = async (bytes: ArrayBuffer) =>
    Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");

export const useCodeOfConduct = (
    token: string | undefined,
    supplierName: string,
    customerName: string,
    signer: { digitalSigning: boolean; signingAs?: string; signerName?: string }
): CodeOfConductSource => {
    const { digitalSigning, signingAs, signerName } = signer;
    return useMemo(
        () => ({
            supplierName,
            customerName,
            key: token,
            digitalSigning,
            signingAs,
            signerName,
            load: async () => {
                const { file } = unwrap(
                    await formsFetch.GET("/api/code-of-conduct", { headers: authHeader(token) }),
                    "get code of conduct"
                );
                if (!file) return { file: null };
                const response = await fetch(file.url);
                if (!response.ok) throw new Error(`Code of Conduct download failed with status ${response.status}`);
                const pdf = isPdf(file.name);
                const bytes = await response.arrayBuffer();
                const blob = new Blob([bytes], { type: pdf ? "application/pdf" : "application/octet-stream" });
                return {
                    file: {
                        name: file.name,
                        blobUrl: URL.createObjectURL(blob),
                        pdf,
                        sha256: await sha256(bytes),
                        sizeBytes: bytes.byteLength,
                    },
                };
            },
        }),
        [token, supplierName, customerName, digitalSigning, signingAs, signerName]
    );
};
